import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LocalizedLink as Link, LocalizedNavLink as NavLink } from "@/components/LocalizedLink";
import { lockScroll, unlockScroll } from "@/lib/scrollLock";
import { AnimatePresence, m } from "framer-motion";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { useLang, SUPPORTED_LANGS } from "@/i18n";

const LANG_LABELS = { it: "Italiano", en: "English", fr: "Français", es: "Español" };

const LangToggle = ({ className = "", dropUp = false }) => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, [open]);

  return (
    <div className={`relative ${className}`} ref={wrapRef} data-testid="language-toggle">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-toggle-trigger"
        className="flex items-center gap-1.5 rounded-full border border-linen bg-cream/60 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-ink transition-colors duration-300 hover:border-terracotta/50 hover:bg-cream"
      >
        {lang}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, y: dropUp ? 6 : -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: dropUp ? 6 : -6 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            role="listbox"
            className={`absolute z-10 min-w-[10.5rem] divide-y divide-linen/60 overflow-hidden rounded-2xl border border-linen bg-cream shadow-[0_16px_40px_-12px_rgba(42,36,33,0.25)] ${
              dropUp ? "left-0 bottom-full mb-2" : "right-0 top-full mt-2"
            }`}
          >
            {SUPPORTED_LANGS.map((l) => (
              <button
                key={l}
                type="button"
                role="option"
                aria-selected={lang === l}
                data-testid={`lang-toggle-${l}`}
                onClick={() => {
                  setLang(l);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors duration-200 ${
                  lang === l ? "bg-terracotta text-cream" : "text-ink hover:bg-sand"
                }`}
              >
                <span className="text-xs font-semibold uppercase tracking-widest">{l}</span>
                <span className={lang === l ? "text-cream/90" : "text-stone"}>{LANG_LABELS[l]}</span>
              </button>
            ))}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Navbar = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);
  const { pathname } = useLocation();

  // Close on any navigation, including a language switch from inside the menu.
  useEffect(() => { setOpen(false); }, [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    lockScroll();
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onDown = (e) => { if (headerRef.current && !headerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      unlockScroll();
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open]);

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home-link" },
    { to: "/unita", label: t.nav.units, testid: "nav-units-link" },
    { to: "/dintorni", label: t.nav.surroundings, testid: "nav-dintorni-link" },
    { to: "/dove-siamo", label: t.nav.where, testid: "nav-dove-siamo-link" },
    { to: "/contatti", label: t.nav.contact, testid: "nav-contatti-link" },
  ];

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-300 hover:text-terracotta ${
      isActive ? "text-terracotta" : "text-ink/70"
    }`;

  return (
    <header ref={headerRef} className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-cream/80 border-b border-linen/60" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="Postumia Place" className="flex items-center gap-2.5 text-ink hover:text-terracotta transition-colors duration-300">
          <img src="/logo.png" alt="" className="h-10 w-auto sm:hidden" />
          <img src="/logo-mark.png" alt="" className="hidden sm:block h-9 w-auto" />
          <span className="font-display italic text-2xl md:text-3xl hidden sm:inline">Postumia Place</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={l.testid} className={linkCls}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <LangToggle />
          <Link
            to="/prenota"
            data-testid="nav-book-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-2.5 text-sm font-medium text-cream hover:bg-terracotta-dark transition-colors duration-300"
          >
            {t.nav.book}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="xl:hidden text-ink p-2"
          aria-label={open ? t.nav.menuClose : t.nav.menuOpen}
          aria-expanded={open}
          aria-controls="mobile-menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="xl:hidden overflow-hidden border-t border-linen/60 bg-cream/95 backdrop-blur-xl"
            id="mobile-menu"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `font-display text-3xl ${isActive ? "text-terracotta" : "text-ink"}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-4 pt-4">
                <LangToggle dropUp />
                <Link
                  to="/prenota"
                  data-testid="mobile-nav-book-cta"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm font-medium text-cream"
                >
                  {t.nav.book}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
